// client/src/components/recursos/Recursos.js
import React, { Fragment, useState, useMemo, useCallback, useEffect } from 'react';
import { useRecursos, useMutateRecursos } from '../../hooks/useRecursos';
import { useCategoriasParaFormulario } from '../../hooks/useCategorias';
import useAlert from '../../hooks/useAlert';
import { useMe } from '../../hooks/useAuth';
import RecursoForm from './RecursoForm';
import RecursoImages from './RecursoImages';
import RecursoRow from './RecursoRow'; 
import FiltroRecursos from './FiltroRecursos'; 
import { Filter as FilterIcon, Loader2, UserCheck, XCircle, AlertCircle, Image as ImageIcon, FileText, PlusCircle, Eye, Trash2 } from 'lucide-react';
import Pagination from '../common/Pagination';
import PageHeader from '../layout/PageHeader';
import FabButton from '../common/FabButton';
import ActionsDrawer from '../common/ActionsDrawer';

const extrairIdReal = (obj) => {
    if (!obj) return null;
    if (typeof obj === 'string') return obj;
    if (obj.$oid) return String(obj.$oid);
    if (obj._id) {
        if (typeof obj._id === 'string') return obj._id;
        if (obj._id.$oid) return String(obj._id.$oid);
        return String(obj._id);
    }
    if (obj.id) {
        if (typeof obj.id === 'string') return obj.id;
        if (obj.id.$oid) return String(obj.id.$oid);
        return String(obj.id);
    }
    return String(obj);
};

const Recursos = () => {
    const { setAlert } = useAlert();
    const { user } = useMe();
    
    const accountId = useMemo(() => extrairIdReal(user?.account), [user]);

    const [isFormVisible, setIsFormVisible] = useState(false);
    const [showFilters, setShowFilters] = useState(false);
    const [currentPage, setCurrentPage] = useState(1);
    const [activeFilters, setActiveFilters] = useState({});
    const [currentRecurso, setCurrentRecurso] = useState(null);
    const [activeTab, setActiveTab] = useState('lista');
    const [recursosProcessados, setRecursosProcessados] = useState([]);
    const [selectedRecurso, setSelectedRecurso] = useState(null);
    const recursosPerPage = 10;

    const queryParams = useMemo(() => ({
        page: currentPage,
        limit: recursosPerPage,
        ...activeFilters
    }), [currentPage, activeFilters]);

    const { 
        data: recursosData, 
        isLoading, 
        refetch,
        isError: isRecursosError,
        error: recursosError
    } = useRecursos(accountId, queryParams);
    
    const { deleteRecurso, isPending: isMutationPending } = useMutateRecursos(accountId);

    const { data: categoriasData, isLoading: loadingCategorias } = useCategoriasParaFormulario(accountId);
    
    const categoriasMap = useMemo(() => {
        if (!categoriasData || !Array.isArray(categoriasData)) return {};
        
        const mapa = {};
        categoriasData.forEach(categoria => {
            const catId = extrairIdReal(categoria);
            if (catId) {
                mapa[catId] = categoria;
            }
        });
        
        return mapa;
    }, [categoriasData]);

    const processarRecursoComImagensECategoria = useCallback((recurso) => {
        if (!recurso) return null;
        
        let categoriaProcessada = recurso.categoria;
        
        if (recurso.categoria) {
            const catId = extrairIdReal(recurso.categoria);
            if (categoriasMap[catId]) {
                categoriaProcessada = categoriasMap[catId];
            } else if (typeof recurso.categoria === 'object') {
                categoriaProcessada = recurso.categoria;
            }
        }

        const recursoProcessado = {
            ...recurso,
            _id: extrairIdReal(recurso),
            imagens: Array.isArray(recurso.imagens) ? recurso.imagens : [],
            imagensDaGaleria: Array.isArray(recurso.imagensDaGaleria) ? recurso.imagensDaGaleria : [],
            imagemPrincipal: recurso.imagemPrincipal || null,
            imagemUrl: recurso.imagemUrl || null,
            categoria: categoriaProcessada,
            // ✅ NOVOS CAMPOS: Pool de recursos
            isGrupoDeRecursos: recurso.isGrupoDeRecursos || false,
            recursosDoGrupo: Array.isArray(recurso.recursosDoGrupo) ? recurso.recursosDoGrupo : [],
            grupoPertencente: recurso.grupoPertencente || null
        };

        if (!recursoProcessado.imagemUrl || recursoProcessado.imagemUrl.includes('data:image/svg+xml')) {
            if (recursoProcessado.imagemPrincipal && recursoProcessado.imagemPrincipal.refId) {
                const principalRefId = recursoProcessado.imagemPrincipal.refId.toString();
                
                if (recursoProcessado.imagens && recursoProcessado.imagens.length > 0) {
                    const imagemPrincipal = recursoProcessado.imagens.find(img => 
                        img._id && img._id.toString() === principalRefId
                    );
                    if (imagemPrincipal && imagemPrincipal.arquivo) {
                        recursoProcessado.imagemUrl = imagemPrincipal.arquivo;
                    }
                }
                
                if (!recursoProcessado.imagemUrl && recursoProcessado.imagensDaGaleria && recursoProcessado.imagensDaGaleria.length > 0) {
                    const imagemPrincipal = recursoProcessado.imagensDaGaleria.find(img => 
                        img._id && img._id.toString() === principalRefId
                    );
                    if (imagemPrincipal && imagemPrincipal.arquivo) {
                        recursoProcessado.imagemUrl = imagemPrincipal.arquivo;
                    }
                }
            }
            
            if (!recursoProcessado.imagemUrl) {
                if (recursoProcessado.imagens && recursoProcessado.imagens.length > 0) {
                    recursoProcessado.imagemUrl = recursoProcessado.imagens[0]?.arquivo;
                } else if (recursoProcessado.imagensDaGaleria && recursoProcessado.imagensDaGaleria.length > 0) {
                    recursoProcessado.imagemUrl = recursoProcessado.imagensDaGaleria[0]?.arquivo;
                }
            }
        }

        return recursoProcessado;
    }, [categoriasMap]);

    useEffect(() => {
        const rawRecursos = (recursosData?.data || recursosData?.recursos || recursosData || []);
        const rawArray = Array.isArray(rawRecursos) ? rawRecursos : [];
        
        if (rawArray.length > 0) {
            const processados = rawArray.map(recurso => 
                processarRecursoComImagensECategoria(recurso)
            ).filter(Boolean);
            
            setRecursosProcessados(processados);
        } else {
            setRecursosProcessados([]);
        }
    }, [recursosData, categoriasMap, processarRecursoComImagensECategoria]);

    const totalRecursos = recursosData?.totalCount || recursosData?.count || recursosData?.total || recursosProcessados.length || 0;
    const pagination = recursosData?.pagination || {};
    const hasError = isRecursosError || recursosData?.success === false;

    const isLoadingCompleto = isLoading || loadingCategorias;

    const handleApplyFilters = useCallback((filters) => { 
        setCurrentPage(1); 
        setActiveFilters(filters); 
    }, []);

    const handleAdicionarClick = useCallback(() => { 
        setCurrentRecurso(null); 
        setActiveTab('form-dados');
        setIsFormVisible(true); 
    }, []);

    const handleCloseForm = useCallback(() => { 
        setIsFormVisible(false); 
        setCurrentRecurso(null);
        setActiveTab('lista');
        refetch(); 
    }, [refetch]);

    const handleSaveSuccess = useCallback((updatedRecurso = null) => {
        const recursoFormatado = processarRecursoComImagensECategoria(updatedRecurso);
        
        if (recursoFormatado) {
            setCurrentRecurso(recursoFormatado);
            setRecursosProcessados(prev => {
                const existe = prev.some(r => r._id === recursoFormatado._id);
                if (existe) {
                    return prev.map(r => r._id === recursoFormatado._id ? recursoFormatado : r);
                }
                return [recursoFormatado, ...prev];
            });
        }

        refetch();
        setAlert('Recurso salvo com sucesso!', 'success');
        
        if (!currentRecurso && recursoFormatado) {
            setCurrentRecurso(recursoFormatado);
            setActiveTab('form-imagens');
        }
    }, [currentRecurso, refetch, setAlert, processarRecursoComImagensECategoria]);

    const handleEditClick = useCallback((recurso) => {
        const recursoFormatado = processarRecursoComImagensECategoria(recurso);
        setCurrentRecurso(recursoFormatado);
        setActiveTab('form-dados');
        setIsFormVisible(true);
        setSelectedRecurso(null);
    }, [processarRecursoComImagensECategoria]);

    const handleEditImagesClick = useCallback((recurso) => {
        if (!recurso._id && !recurso.id) {
            setAlert('Recurso sem ID válido para editar imagens', 'danger');
            return;
        }

        const recursoFormatado = processarRecursoComImagensECategoria(recurso);
        setCurrentRecurso(recursoFormatado);
        setActiveTab('form-imagens');
        setIsFormVisible(true);
        setSelectedRecurso(null);
    }, [setAlert, processarRecursoComImagensECategoria]);

    const handleDelete = useCallback((recursoId) => {
        if (!recursoId) {
            setAlert('ID do recurso não informado', 'danger');
            return;
        }

        const idString = String(recursoId);
        const isValidId = idString.match(/^[0-9a-fA-F]{24}$/);
        
        if (!isValidId) {
            setAlert('ID do recurso inválido para exclusão', 'danger');
            return;
        }

        if (window.confirm('Tem certeza que deseja remover este recurso? Esta ação não pode ser desfeita.')) {
            deleteRecurso.mutate(idString, {
                onSuccess: () => {
                    setAlert('Recurso removido com sucesso!', 'success');
                    setSelectedRecurso(null);

                    if (recursosProcessados.length === 1 && currentPage > 1) {
                        setCurrentPage(prev => prev - 1);
                    } else {
                        refetch();
                    }
                    
                    if (currentRecurso && (currentRecurso._id === idString || currentRecurso.id === idString)) {
                        handleCloseForm();
                    }
                },
                onError: (error) => {
                    const errorMsg = error.response?.data?.error || error.response?.data?.message || error.message || 'Erro ao remover recurso';
                    setAlert(errorMsg, 'danger');
                }
            });
        }
    }, [deleteRecurso, refetch, setAlert, currentRecurso, handleCloseForm, recursosProcessados.length, currentPage]);

    const handleTabChange = useCallback((tab) => {
        setActiveTab(tab);
    }, []);

    const handleRecursoUpdate = useCallback((updatedRecurso) => {
        const recursoFormatado = processarRecursoComImagensECategoria(updatedRecurso);
        setCurrentRecurso(recursoFormatado);
        
        setRecursosProcessados(prev => 
            prev.map(r => r._id === recursoFormatado._id ? recursoFormatado : r)
        );
        refetch();
    }, [refetch, processarRecursoComImagensECategoria]);

    const renderActiveContent = () => {
        if (!isFormVisible) return null;

        return (
            <Fragment>
                {currentRecurso && (
                    <div className="tabs-nav mb-4 border-b border-gray-200">
                        {/* .tab-button ja tem gap:0.5rem entre icone e texto (_tables.scss)
                            — o mr-2 duplicava o espacamento. Tamanho alinhado aos 18px
                            padrao (estava em 16px, unico caso divergente de tab-button
                            no app). */}
                        <button
                            className={`tab-button flex items-center py-2 px-4 font-medium text-sm border-b-2 transition-colors ${activeTab === 'form-dados' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                            onClick={() => handleTabChange('form-dados')}
                        >
                            <FileText className="icon" size={18}/> Dados do Recurso
                        </button>
                        <button
                            className={`tab-button flex items-center py-2 px-4 font-medium text-sm border-b-2 transition-colors ${activeTab === 'form-imagens' ? 'border-primary text-primary' : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'}`}
                            onClick={() => handleTabChange('form-imagens')}
                        >
                            <ImageIcon className="icon" size={18}/> Imagens da Galeria
                        </button>
                    </div>
                )}

                <div className="content-panel bg-white p-6 rounded-lg border border-gray-200 shadow-sm">
                    {activeTab === 'form-dados' && (
                        <RecursoForm 
                            key={currentRecurso?._id || 'new'}
                            onCloseForm={handleCloseForm}
                            onSaveSuccess={handleSaveSuccess}
                            onTabChange={handleTabChange}
                            current={currentRecurso}
                            accountId={accountId}
                        />
                    )}
                    
                    {activeTab === 'form-imagens' && currentRecurso && (
                        <RecursoImages 
                            recurso={currentRecurso}
                            isCreating={false}
                            onUpdate={handleRecursoUpdate}
                            onClose={handleCloseForm}
                            onBackToForm={() => setActiveTab('form-dados')}
                        />
                    )}
                </div>
            </Fragment>
        );
    };

    return (
        <Fragment>
            <div className="page-management-container">
                <PageHeader
                    icon={<UserCheck />}
                    title="Gerenciamento de Recursos"
                    actions={<>
                        {!isFormVisible && (
                            <>
                                <button
                                    onClick={() => setShowFilters(p => !p)}
                                    className={`action-button ${showFilters ? 'action-button-primary' : 'action-button-secondary'}`}
                                    disabled={isLoadingCompleto || isMutationPending}
                                >
                                    <FilterIcon size={18} className="icon"/>
                                    {showFilters ? 'Ocultar Filtros' : 'Filtros'}
                                </button>
                            </>
                        )}
                        {isFormVisible && (
                            <button
                                onClick={handleCloseForm}
                                className="action-button action-button-secondary"
                                disabled={isMutationPending}
                            >
                                <XCircle size={18} className="icon"/>
                                Voltar para Lista
                            </button>
                        )}
                    </>}
                />

                {!isFormVisible && (
                    <Fragment>
                        {showFilters && (
                            <div className="content-panel mb-6">
                                <h3 className="panel-title mb-4 font-semibold text-gray-700">Filtrar Recursos</h3>
                                <FiltroRecursos 
                                    onApplyFilters={handleApplyFilters} 
                                    loading={isLoadingCompleto || isMutationPending} 
                                    accountId={accountId} 
                                /> 
                            </div>
                        )}

                        <div className="table-container bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                            <div className="table-wrapper p-0">
                                {isLoadingCompleto && recursosProcessados.length === 0 ? (
                                    <div className="table-loading-state py-12 flex flex-col items-center text-gray-500">
                                        <Loader2 size={32} className="animate-spin mb-3 text-primary"/> 
                                        <p>{loadingCategorias ? 'Carregando categorias...' : 'Carregando recursos...'}</p>
                                    </div>
                                ) : recursosProcessados.length > 0 ? (
                                    <div className="overflow-x-auto">
                                        <table className="styled-table w-full text-left border-collapse">
                                            <thead>
                                                <tr className="bg-gray-50 text-gray-500 text-xs uppercase tracking-wider">
                                                    <th className="px-6 py-3 border-b border-gray-200 font-medium">Nome</th>
                                                    <th className="px-6 py-3 border-b border-gray-200 font-medium">Tipo</th>
                                                    <th className="px-6 py-3 border-b border-gray-200 font-medium">Categoria</th>
                                                    <th className="px-6 py-3 border-b border-gray-200 font-medium">Status</th>
                                                    <th className="px-6 py-3 border-b border-gray-200 font-medium">Pool</th>
                                                    <th className="px-6 py-3 border-b border-gray-200 font-medium text-right">Ações</th>
                                                </tr>
                                            </thead>
                                            <tbody className="divide-y divide-gray-200">
                                                {recursosProcessados.map(recurso => (
                                                    <RecursoRow
                                                        key={recurso._id}
                                                        recurso={recurso}
                                                        onOpenActions={setSelectedRecurso}
                                                        loading={isMutationPending}
                                                    />
                                                ))}
                                            </tbody>
                                        </table>
                                    </div>
                                ) : (
                                    <div className="table-empty-state py-12 px-6 flex flex-col items-center justify-center text-center">
                                        <AlertCircle size={48} className="text-gray-300 mb-4" />
                                        <h4 className="text-lg font-medium text-gray-800 mb-2">Nenhum recurso encontrado</h4>
                                        <p className="text-sm text-gray-500 max-w-md mx-auto mb-6">
                                            {Object.keys(activeFilters).length > 0 
                                                ? 'Nenhum recurso corresponde aos filtros aplicados.' 
                                                : 'Sua lista de recursos e salas ainda está vazia. Comece criando seu primeiro recurso.'
                                            }
                                        </p>
                                        {/* action-button-primary ja da conta de padding/cor/
                                            sombra/peso da fonte — as classes Tailwind eram
                                            redundantes com o mixin (coincidiam por acaso, mas
                                            confundiam a leitura do componente) */}
                                        <button
                                            onClick={handleAdicionarClick}
                                            className="action-button action-button-primary"
                                            disabled={isMutationPending}
                                        >
                                            <PlusCircle size={18} className="icon" />
                                            {isMutationPending ? 'Processando...' : 'Criar Primeiro Recurso'}
                                        </button>
                                    </div>
                                )}
                            </div>
                            
                            {totalRecursos > recursosPerPage && !isLoadingCompleto && (
                                <div className="border-t border-gray-200 px-6 py-3 bg-white">
                                    <Pagination 
                                        {...pagination} 
                                        totalItems={totalRecursos} 
                                        onPageChange={setCurrentPage} 
                                        currentPage={currentPage}
                                    />
                                </div>
                            )}
                        </div>
                    </Fragment>
                )}

                {renderActiveContent()}
            </div>

            <FabButton onClick={handleAdicionarClick} title="Adicionar Recurso" visible={!isFormVisible} disabled={isLoadingCompleto || isMutationPending} icon={<UserCheck size={26} className="icon"/>} />

            <ActionsDrawer
                open={!!selectedRecurso}
                onClose={() => setSelectedRecurso(null)}
                title={selectedRecurso?.nome || 'Recurso'}
                actions={selectedRecurso ? [
                    {
                        key: 'imagens',
                        label: 'Gerenciar Imagens',
                        icon: <ImageIcon size={20} className="icon" />,
                        onClick: () => handleEditImagesClick(selectedRecurso),
                    },
                    {
                        key: 'editar',
                        label: 'Visualizar/Editar',
                        icon: <Eye size={20} className="icon" />,
                        onClick: () => handleEditClick(selectedRecurso),
                    },
                    {
                        key: 'excluir',
                        label: 'Excluir',
                        icon: <Trash2 size={20} className="icon" />,
                        onClick: () => handleDelete(selectedRecurso._id || selectedRecurso.id),
                        destructive: true,
                        loading: isMutationPending,
                    },
                ] : []}
            />
        </Fragment>
    );
};

export default Recursos;